import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {combineLatest, Observable, of} from "rxjs";
import {map, mergeAll, switchMap, tap} from "rxjs/operators";
import {fromArray} from "rxjs/internal/observable/fromArray";
import {Page, Pageable} from "../../model/rest/base";
import {V3Student} from "../../model/v3/schools";
import {StudentV3RestService} from "../../services/rest/v3/student-rest.service";
import {CspaRestServiceV3} from "../../services/rest/v3/cspa-rest.service";
import {ItemAvailability, StructItemBaseTyped, V3Chapter, V3ExerciseSession, V3ExerciseSet} from "../../model/v3/cspa";

@Component({
    selector: 'app-manager-student-cspa-monitor',
    templateUrl: './manager-student-cspa-monitor.component.html',
    styleUrls: ['./manager-student-cspa-monitor.component.scss'],
    standalone: false
})
export class ManagerStudentCspaMonitorComponent implements OnInit {
  schoolId: number;
  exerciseSets: Map<string, V3ExerciseSet> = null;
  chaptersCache = new Map<string, V3Chapter[]>();
  private student: V3Student;
  setAvailabilities: ItemAvailability[] = [];
  innerAvailabilities: Map<string, ItemAvailability> = new Map<string, ItemAvailability>();
  private setSessions: Map<string, Page<V3ExerciseSession>> = new Map<string, Page<V3ExerciseSession>>();

  constructor(
    private activeRoute: ActivatedRoute,
    private cspa: CspaRestServiceV3,
    private studentV3Rest: StudentV3RestService
    ) { }

  ngOnInit() {

    combineLatest(
      [this.activeRoute.parent.parent.paramMap,
      this.activeRoute.parent.paramMap]
    ).pipe(
      map(([parentParams, params]) => [Number(parentParams.get('schoolId')), Number(params.get('studentId'))]),
      tap( ([schoolId,_]) => {
        this.schoolId = schoolId;
      }),
      switchMap(([schoolId, studentId]) =>
        this.studentV3Rest.getStudent(schoolId, studentId)
      ),
      tap(student => this.student = student),
      switchMap( _ => this.loadExerciseSets()),
      switchMap( _ => this.getAvailabilities('', 1)),
      map<ItemAvailability[], ItemAvailability[]>( allSetAvailabilities =>
        allSetAvailabilities.filter(it => it.isAssigned || it.isAvailable && this.exerciseSets.has(it.path))
      ),
      tap<ItemAvailability[]>(setAvailabilities =>
        this.setAvailabilities = setAvailabilities
      ),
      switchMap<ItemAvailability[], Observable<Observable<[V3Chapter[], ItemAvailability[], Page<V3ExerciseSession>]>>>(availabilities =>
        fromArray(availabilities.map( availability =>
          combineLatest([
            this.loadExerciseSetStructure(availability.path),
            this.loadInnerAvailabilities(availability.path),
            this.loadSetSessionsPage(availability.path)
          ])
        ))
      ),
      mergeAll()
    ).subscribe(  );
  }

  loadInnerAvailabilities(path: string) {
    return this.getAvailabilities(path, 3).pipe(
      tap( setInnerAvailabilities => setInnerAvailabilities.forEach( av => this.innerAvailabilities.set(av.path, av)))
    );
  }

  getAvailabilities(path: string, depth: number) {
    return this.cspa.listPersonAvailabilities(this.student.person.id, path, depth)
  }

  loadExerciseSetStructure(path: string): Observable<V3Chapter[]> {
    if (this.chaptersCache.has(path)) {
      return of(this.chaptersCache.get(path));
    }
    return this.cspa.listChapters(path + "_").pipe(
      tap( setChapters => this.chaptersCache.set(path, setChapters))
    )
  }

  loadExerciseSets(): Observable<any> {
    if (this.exerciseSets && this.exerciseSets.size > 0) return of(this.exerciseSets)
    return this.cspa.listExerciseSets().pipe(
      map( sets =>
        sets.reduce((res,current)=> res.set(current.path, current)
          ,new Map<string, V3ExerciseSet>())),
      tap( setsMap => this.exerciseSets = setsMap)
    )
  }

  getSetName(availability: ItemAvailability) {
    return this.exerciseSets.get(availability.path).name;
  }

  getItemLastUpdate(availability: ItemAvailability) {
    return availability.lastUpdate;
  }

  listAvailableStages(path: string): V3Chapter[] {
    const setChapters = this.chaptersCache.get(path);
    if (!setChapters) return [];
    return setChapters.filter(
      chapter => {
        const av = this.innerAvailabilities.get(chapter.path);
        if (!av) return false;
        return av.isAvailable || av.isAssigned;
      }
    );
  }

  getItemShortName(item: StructItemBaseTyped) {
    return item.shortName;
  }

  getItemByPath(path: string) {
    return this.innerAvailabilities.get(path);
  }

  getStatusClass(path: string) {
    const availability = this.innerAvailabilities.get(path);

    if (!availability) {
      return 'locked';
    }
    if (!availability.isAssigned || !availability.isAvailable) {
      return 'locked';
    }
    if (availability.score === 0) {
      return 'ready';
    } else if (availability.score < 0.5) {
      return 'bad';
    } else if (availability.score < 0.75) {
      return 'enough';
    } else if (availability.score < 0.85) {
      return 'good';
    } else {
      return 'perfect';
    }
  }

  private loadSetSessionsPage(path: string): Observable<Page<V3ExerciseSession>> {
    return this.cspa.findSessions(this.student.person.id, `${path}_`, Pageable.of(0, 5, ["finishDate,DESC"])).pipe(
      tap<Page<V3ExerciseSession>>(setSessions => {
        this.setSessions.set(path, setSessions)
      })
    );
  }

  countSetSessions(availability: ItemAvailability) {
    if (!availability) return 0;
    const setSessionsPage = this.setSessions.get(availability.path);
    if (!setSessionsPage) return 0;
    return setSessionsPage.totalElements;
  }

  getSetSessions(setAvailability: ItemAvailability): V3ExerciseSession[] {
    const setSessions = this.setSessions.get(setAvailability.path);
    if (!setSessions) return [];
    return setSessions.content;
  }

  getChapterHeader(setAvailability: ItemAvailability) {
    return this.getSetSessions(setAvailability)[0].chapterName.split(' ')[0]

  }

  getExerciseNameHeader(setAvailability: ItemAvailability) {
    return this.getSetSessions(setAvailability)[0].exerciseName.split(' ')[0]
  }

  getChapter(session: V3ExerciseSession) {
    return session.chapterName.split(' ')[1]

  }

  getSection(session: V3ExerciseSession) {
    return session.sectionName.split(',')[0]

  }

  getExerciseName(session: V3ExerciseSession) {
    return session.exerciseName.split(' ')[1]
  }

  getSessionDate(session: V3ExerciseSession) {
    return session.finishDate;
  }

  isExam(path: string) {
    return path.includes("exam")
  }
}
